import { useEffect, useId, useMemo, useRef, useState } from "react";

import directorApi from "@contexts/director/api";

import {
  bpsToInput,
  parseInteger,
  parsePercent,
  premisesBodyFromStored,
  provisionalRateNotice,
} from "../dashboard/PremisesPanel";

// Parâmetros dos cenários Conservador e Estresse (SPEC 2026-10-21 item 9.4). O painel só converte
// unidades (% ↔ bps) e espelha os limites do BE para dar retorno imediato; o cálculo é do BE.

const SCENARIOS = [
  ["conservative", "Conservador"],
  ["stress", "Estresse"],
];
const MAX_REASON_LENGTH = 500;

const DAYS_ERROR = "Informe de 0 a 365 dias.";
const PERCENT_ERROR = "Informe um percentual de 0 a 100.";
const SAVED = "Cenários salvos.";
const UNCHANGED = "Cenários salvos. Nenhum parâmetro mudou.";
const SAVE_FAILED = "Não foi possível salvar os cenários. Tente novamente.";
const LOAD_FAILED = "Não foi possível carregar os parâmetros dos cenários.";
const STORED_INVALID = "As premissas salvas não puderam ser reenviadas. Ajuste-as no dashboard antes de alterar os cenários.";
const READ_ONLY = "Você não pode alterar os cenários desta empresa. Peça a quem administra as premissas da diretoria.";
const PORTFOLIO_DEFAULT = "Usa o padrão do portfólio.";

const blank = (text) => typeof text !== "string" || text.trim() === "";

const PARAM_KEYS = ["inflowDelayDays", "futureInflowHaircutBps", "uncommittedOutflowUpliftBps"];
const isScenario = (value) => Boolean(value) && PARAM_KEYS.every((key) => Number.isSafeInteger(value[key]));

// Valores do formulário a partir de um cenário (salvo ou do portfólio); sem cenário, vazios.
const valuesFrom = (scenario) => (isScenario(scenario)
  ? {
    delay: String(scenario.inflowDelayDays),
    haircut: bpsToInput(scenario.futureInflowHaircutBps),
    uplift: bpsToInput(scenario.uncommittedOutflowUpliftBps),
  }
  : { delay: "", haircut: "", uplift: "" });

// Marcado, mostra o que vale de fato (o portfólio, vindo do BE); desmarcar parte desses valores.
const formFromScenario = (scenario, portfolio) => (scenario
  ? { portfolio: false, ...valuesFrom(scenario) }
  : { portfolio: true, ...valuesFrom(portfolio) });

const formFromPremises = (premises, portfolioScenarios) => Object.fromEntries(
  SCENARIOS.map(([key]) => [key, formFromScenario(premises?.scenarios?.[key], portfolioScenarios?.[key])]),
);

// Q7: o BE só renova o carimbo quando os valores mudam; mudou = entrou, saiu, ou valor/carimbo novo.
const sameScenario = (before, after) => {
  if (!before || !after) return !before && !after;
  return before.updatedAt === after.updatedAt && PARAM_KEYS.every((key) => before[key] === after[key]);
};
export const changedScenarios = (before, after) => SCENARIOS
  .map(([key]) => key)
  .filter((key) => !sameScenario(before?.[key], after?.[key]));

const scenarioName = (key) => SCENARIOS.find(([value]) => value === key)?.[1] ?? null;

// `null` volta ao portfólio; senão os três valores, com erro por campo.
const buildScenario = (fields) => {
  if (fields.portfolio) return { value: null, errors: {} };
  const errors = {};
  const take = (name, result) => {
    if (result.error) errors[name] = result.error;
    return result.value;
  };
  const check = (name, parse) => (blank(fields[name]) ? take(name, { error: name === "delay" ? DAYS_ERROR : PERCENT_ERROR }) : take(name, parse(fields[name])));
  const inflowDelayDays = check("delay", (text) => parseInteger(text, 0, 365, DAYS_ERROR));
  const futureInflowHaircutBps = check("haircut", (text) => parsePercent(text, 10000, PERCENT_ERROR));
  const uncommittedOutflowUpliftBps = check("uplift", (text) => parsePercent(text, 10000, PERCENT_ERROR));
  if (Object.keys(errors).length) return { value: undefined, errors };
  return { value: { inflowDelayDays, futureInflowHaircutBps, uncommittedOutflowUpliftBps }, errors };
};

const errorMessage = (result) => {
  const message = typeof result?.body?.message === "string" ? result.body.message : null;
  if (result?.status === 401) return "Faça login novamente para salvar os cenários.";
  if ([403, 409, 422].includes(result?.status) && message) return message;
  return SAVE_FAILED;
};

const NumberField = ({ id, label, value, onChange, error, readOnly, inputMode }) => <div className="director-premises__field">
  <label className="director-premises__label" htmlFor={id}>{label}</label>
  <input
    className="director-premises__input"
    id={id}
    type="text"
    inputMode={inputMode}
    value={value}
    readOnly={readOnly}
    aria-invalid={error ? "true" : undefined}
    aria-describedby={error ? `${id}-error` : undefined}
    onChange={(event) => onChange(event.target.value)}
  />
  {error && <small className="director-premises__error" id={`${id}-error`}>{error}</small>}
</div>;

// `readOnly` e `readOnlyHint`: como no painel de premissas (a página Premissas e cenários).
export const ScenariosPanelBody = ({ companyId, onSaved, api = directorApi, readOnly: forcedReadOnly = false, readOnlyHint = READ_ONLY }) => {
  const prefix = `director-scenarios${useId().replace(/:/g, "-")}`;
  const [revision, setRevision] = useState(0);
  const [load, setLoad] = useState({ status: "loading", premises: null, canWrite: false, portfolioScenarios: null });
  const [form, setForm] = useState(() => formFromPremises(null, null));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let active = true;
    if (!companyId) return () => { active = false; };
    setLoad((current) => ({ ...current, status: "loading" }));
    Promise.resolve(api.fetchDirectorPremises(companyId)).then((result) => {
      if (!active) return;
      const premises = result?.ok ? result.body?.premises : null;
      if (!premises) {
        setLoad({ status: "error", premises: null, canWrite: false, portfolioScenarios: null });
        return;
      }
      const portfolioScenarios = result.body.portfolioScenarios ?? null;
      setLoad({ status: "ready", premises, canWrite: result.body.canWrite === true, portfolioScenarios });
      setForm(formFromPremises(premises, portfolioScenarios));
    }).catch(() => {
      if (active) setLoad({ status: "error", premises: null, canWrite: false, portfolioScenarios: null });
    });
    return () => { active = false; };
  }, [api, companyId, revision]);

  const { premises, canWrite, portfolioScenarios } = load;
  const readOnly = forcedReadOnly || !canWrite;

  const built = useMemo(() => {
    const scenarios = {};
    const errors = {};
    SCENARIOS.forEach(([key]) => {
      const result = buildScenario(form[key]);
      errors[key] = result.errors;
      scenarios[key] = result.value;
    });
    const valid = SCENARIOS.every(([key]) => !Object.keys(errors[key]).length);
    return { scenarios, errors, valid };
  }, [form]);

  // O PUT substitui taxa, imposto e obras: reenvia o que está salvo, sem tocar.
  const storedBody = useMemo(() => (premises ? premisesBodyFromStored(premises) : null), [premises]);

  const setField = (key, field, value) => setForm((current) => ({
    ...current,
    [key]: { ...current[key], [field]: value },
  }));

  // Marcar volta a mostrar o portfólio; desmarcar mantém o que está na tela (o portfólio).
  const setPortfolio = (key, checked) => setForm((current) => ({
    ...current,
    [key]: checked
      ? { portfolio: true, ...valuesFrom(portfolioScenarios?.[key]) }
      : { ...current[key], portfolio: false },
  }));

  const save = async (event) => {
    event.preventDefault();
    if (!built.valid || saving || readOnly) return;
    if (!storedBody) {
      setNotice({ tone: "error", text: STORED_INVALID });
      return;
    }
    setSaving(true);
    setNotice(null);
    const trimmed = reason.trim();
    let result;
    try {
      result = await api.saveDirectorPremises(companyId, {
        ...storedBody,
        scenarios: built.scenarios,
        ...(trimmed ? { reason: trimmed } : {}),
      });
    } catch {
      result = null;
    }
    setSaving(false);
    if (result?.ok && result.body?.premises) {
      const nextPortfolio = result.body.portfolioScenarios ?? portfolioScenarios;
      const changed = changedScenarios(premises.scenarios, result.body.premises.scenarios);
      setLoad({ status: "ready", premises: result.body.premises, canWrite: result.body.canWrite === true, portfolioScenarios: nextPortfolio });
      setForm(formFromPremises(result.body.premises, nextPortfolio));
      setReason("");
      // A página escolhe o cenário que passa a mostrar e devolve a chave; o aviso diz qual é.
      const shown = scenarioName(onSaved?.(changed));
      let text = SAVED;
      if (!changed.length) text = UNCHANGED;
      else if (shown) text = `${SAVED} Mostrando o cenário ${shown} recalculado.`;
      setNotice({ tone: "success", text });
      return;
    }
    setNotice({ tone: "error", text: errorMessage(result) });
    if (result?.status === 409) setRevision((value) => value + 1);
  };

  if (load.status === "error") {
    return <div className="director-premises">
      <p className="director-premises__notice" data-tone="error" role="alert">{LOAD_FAILED}</p>
      <button
        className="director-link-button director-link-button--secondary director-premises__retry"
        type="button"
        onClick={() => setRevision((value) => value + 1)}
      >
        Tentar de novo
      </button>
    </div>;
  }

  if (!premises) {
    return <div className="director-premises">
      <p className="director-premises__status" role="status">Carregando parâmetros…</p>
    </div>;
  }

  return <form className="director-premises director-scenarios__form" onSubmit={save} noValidate>
    <p className="director-premises__hint">
      Parâmetros aplicados ao fluxo de caixa de 13 semanas. Marcando “Usar os parâmetros do portfólio”, o cenário volta ao padrão do portfólio.
    </p>
    {readOnly && readOnlyHint && <p className="director-premises__hint">{readOnlyHint}</p>}
    {notice && <p
      className="director-premises__notice"
      data-tone={notice.tone}
      role={notice.tone === "error" ? "alert" : "status"}
    >
      {notice.text}
    </p>}

    {SCENARIOS.map(([key, label]) => {
      const fields = form[key];
      const errors = built.errors[key];
      const id = (name) => `${prefix}-${key}-${name}`;
      const locked = readOnly || fields.portfolio;
      return <fieldset className="director-premises__company" key={key}>
        <legend className="director-premises__legend">{label}</legend>
        <div className="director-scenarios__toggle">
          <input
            id={id("portfolio")}
            type="checkbox"
            checked={fields.portfolio}
            disabled={readOnly}
            onChange={(event) => setPortfolio(key, event.target.checked)}
          />
          <label className="director-premises__label" htmlFor={id("portfolio")}>Usar os parâmetros do portfólio</label>
        </div>
        {fields.portfolio && !isScenario(portfolioScenarios?.[key])
          ? <p className="director-premises__hint">{PORTFOLIO_DEFAULT}</p>
          : <div className="director-premises__grid">
          <NumberField
            id={id("delay")}
            label="Atraso das entradas (dias)"
            value={fields.delay}
            error={fields.portfolio ? null : errors.delay}
            readOnly={locked}
            inputMode="numeric"
            onChange={(value) => setField(key, "delay", value)}
          />
          <NumberField
            id={id("haircut")}
            label="Corte das entradas (%)"
            value={fields.haircut}
            error={fields.portfolio ? null : errors.haircut}
            readOnly={locked}
            inputMode="decimal"
            onChange={(value) => setField(key, "haircut", value)}
          />
          <NumberField
            id={id("uplift")}
            label="Acréscimo da saída não comprometida (%)"
            value={fields.uplift}
            error={fields.portfolio ? null : errors.uplift}
            readOnly={locked}
            inputMode="decimal"
            onChange={(value) => setField(key, "uplift", value)}
          />
        </div>}
      </fieldset>;
    })}

    {!readOnly && <div className="director-premises__footer">
      <div className="director-premises__field director-premises__field--wide">
        <label className="director-premises__label" htmlFor={`${prefix}-reason`}>Motivo (opcional)</label>
        <textarea
          className="director-premises__input director-premises__textarea"
          id={`${prefix}-reason`}
          value={reason}
          maxLength={MAX_REASON_LENGTH}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      <button
        className="director-link-button director-premises__save"
        type="submit"
        disabled={!built.valid || saving}
      >
        {saving ? "Salvando…" : "Salvar cenários"}
      </button>
      {premises.version === 0 && <p className="director-premises__hint">
        {provisionalRateNotice(bpsToInput(premises.discountRateBps))}
      </p>}
    </div>}
  </form>;
};

// Abre/fecha como o painel de premissas do dashboard; trocar de empresa remonta o corpo.
const ScenariosPanel = ({ companyId, onSaved, api }) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const panelId = `${useId().replace(/:/g, "-")}-scenarios-panel`;

  useEffect(() => {
    if (open) panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [open]);

  return <section className="director-scenarios" aria-label="Ajustar cenários">
    <button
      className="director-link-button director-link-button--secondary director-scenarios__action"
      type="button"
      aria-expanded={open}
      aria-controls={panelId}
      onClick={() => setOpen((value) => !value)}
    >
      {open ? "Fechar" : "Ajustar cenários"}
    </button>
    {open && <div className="director-section director-scenarios__panel" id={panelId} ref={panelRef}>
      <ScenariosPanelBody key={companyId ?? ""} companyId={companyId} onSaved={onSaved} api={api} />
    </div>}
  </section>;
};

export default ScenariosPanel;
